/**
 * **SOUL SHARDS — how a Keeper finds the Suikamon they do not have yet.** (The Suikadex.)
 *
 * `tracks` in code and the database; **Soul Dust** to the player (`CURRENCY_NAME.tracks`,
 * renamed Soul Dust 2026-10-17 so they never collide with the story's 19 Soul Shards). Collect enough
 * of one species' Soul Dust and you find its egg; the Suikadex hatches it.
 *
 * **SCARCITY IS THE POINT.** Epic and Legendary Suikamon are meant to be genuinely hard to find —
 * they are what a future marketplace trades — so a drop's species is decided by RARITY first,
 * with a fixed share of every drop per rarity ({@link TRACK_DROP_SHARE}). How fast one species is
 * found is then `its share of drops ÷ the shards it needs` ({@link findRatePerShard}).
 *
 * **Shards of a Suikamon you already own become Suika** ({@link TRACK_SUIKA_VALUE}). They used to
 * be redirected to a species you had not found, which quietly made rare species easier to find
 * the more common ones you owned — the opposite of scarcity.
 *
 * Pure and RNG-injected: the server rolls (`battleRewardService`, `campaignService`), the client
 * only reads these numbers to draw the rings. Design:
 * docs/superpowers/specs/2026-09-28-soul-shard-scarcity.md.
 *
 * Not to be confused with the SKILL currency — `soul_shards` in code, **Scrolls** to the player.
 */

import { Rarity, type SuikamonSpecies } from './petModels.js';
import { SUIKAMON_SPECIES } from './speciesConfig.js';

/**
 * **SOUL SHARDS TO FIND ONE SPECIES, BY RARITY.** The price of an egg; how OFTEN a species'
 * shards drop is {@link TRACK_DROP_SHARE}, and the two together are its find rate.
 */
export const TRACKS_TO_FIND: Record<Rarity, number> = {
  [Rarity.Common]: 15,
  [Rarity.Rare]: 30,
  [Rarity.Epic]: 50,
  [Rarity.Legendary]: 80,
  [Rarity.Unique]: 120,
};

/**
 * **A WARDEN'S GIFT, BY RARITY** — the Soul Dust a soul node pays on its FIRST clear, in that
 * Warden's species. A saved Warden shows you where its kin roam, but a Legendary's kin roam far:
 * the gift shrinks with rarity, so one story run brings a Common within reach and a Legendary
 * only a step closer.
 */
export const TRACKS_SOUL_FIRST_CLEAR: Record<Rarity, number> = {
  [Rarity.Common]: 8,
  [Rarity.Rare]: 6,
  [Rarity.Epic]: 4,
  [Rarity.Legendary]: 2,
  [Rarity.Unique]: 2,
};

/** Soul Dust a soul node's first clear pays for this Warden's species. */
export function tracksForSoulFirstClear(species: Pick<SuikamonSpecies, 'rarity'>): number {
  return TRACKS_SOUL_FIRST_CLEAR[species.rarity];
}

/**
 * **A TRAIL GIFT, BY RARITY** — the Soul Dust one campaign gift step (🎁) entrusts, in the
 * species it names. Gifts used to hand over 10-55 Suika or three Scrolls: small change in an
 * economy where a day's login pays ~100 and a card copy costs 1000, so opening one was a shrug.
 * A gift is now a step toward an EGG — the thing the whole collection is about — and it shrinks
 * with rarity for the same reason a Warden's gift does: one story run brings a Common within
 * reach and a Legendary only a step closer. A world's big gift (the Soul Token) pays
 * {@link GIFT_TRACKS_BIG_MULTIPLIER}× this.
 */
export const TRACKS_GIFT: Record<Rarity, number> = {
  [Rarity.Common]: 5,
  [Rarity.Rare]: 4,
  [Rarity.Epic]: 3,
  [Rarity.Legendary]: 2,
  [Rarity.Unique]: 2,
};

/** The world's headline gift (the Soul Token) pays this many times an ordinary trail gift. */
export const GIFT_TRACKS_BIG_MULTIPLIER = 2;

/** Soul Dust a trail gift of this species pays; `big` for a world's Soul Token. */
export function tracksForGift(species: Pick<SuikamonSpecies, 'rarity'>, big = false): number {
  return TRACKS_GIFT[species.rarity] * (big ? GIFT_TRACKS_BIG_MULTIPLIER : 1);
}

/**
 * **THE SHARE OF EVERY SOUL SHARD DROP THAT BELONGS TO EACH RARITY**, in percent, split evenly
 * across that rarity's species.
 *
 * This is the scarcity knob, stated the way a player asks it: "what are the odds a shard is
 * Legendary?". Unique has no species yet, so it gets nothing — and a rarity with no species is
 * skipped anyway, so a drop is never wasted on something nobody can find.
 */
export const TRACK_DROP_SHARE: Record<Rarity, number> = {
  [Rarity.Common]: 50,
  [Rarity.Rare]: 27,
  [Rarity.Epic]: 20,
  [Rarity.Legendary]: 3,
  [Rarity.Unique]: 0,
};

/**
 * **THE SUIKAMON YOU BEAT COUNTS EXTRA WITHIN ITS RARITY.** Once a drop's rarity is rolled, its
 * species is picked among that rarity with the defeated opponent weighted this many times — the
 * fiction holds (beating a Kobu tends to leave Kobu shards) without the opponent ever changing
 * how RARE the drop is. Farming a Legendary does not make Legendary shards more common.
 */
export const TRACK_NATURAL_WEIGHT = 2;

/**
 * **SUIKA PER SOUL SHARD OF A SUIKAMON YOU ALREADY OWN**, by rarity. A duplicate is small change,
 * a little more for a rarer one — never nothing, never a reason to farm.
 */
export const TRACK_SUIKA_VALUE: Record<Rarity, number> = {
  [Rarity.Common]: 2,
  [Rarity.Rare]: 3,
  [Rarity.Epic]: 5,
  [Rarity.Legendary]: 10,
  [Rarity.Unique]: 10,
};

/**
 * **A GIFTED SOUL SHARD OF A SUIKAMON YOU ALREADY OWN IS WORTH THIS MANY TIMES A DROPPED ONE.**
 *
 * Soul Dust reaches a player. A chest DROP is repeatable — farmable — so a duplicate
 * stays small change ({@link TRACK_SUIKA_VALUE}). A GIFT (a trail 🎁, or a Warden's first-clear
 * gift) happens once per account and can never be farmed, so the player who already owns that
 * Suikamon must not be told "here is 16 Suika" at the moment the story hands them something big.
 * At ×10 a gifted Common shard is 20 Suika: a trail gift of an owned Common pays 100, a Soul
 * Token 200, a saved Warden 160 — a good day's login each (`DAILY_LOGIN_REWARDS` tops out at 150
 * before day 10), and the whole campaign's gifts, all duplicates, ~4-5k Suika over weeks of play:
 * four or five card copies (`CARD_COPY_SUIKA_PRICE` 1000), never a shortcut past them.
 */
export const GIFT_SUIKA_MULTIPLIER = 10;

/** How a Soul Dust reached the player — decides what a duplicate is worth. */
export type TrackSource = 'drop' | 'gift';

/** Suika per duplicate Soul Dust of this rarity, by how it was earned. */
export function trackSuikaValue(rarity: Rarity, source: TrackSource = 'drop'): number {
  return TRACK_SUIKA_VALUE[rarity] * (source === 'gift' ? GIFT_SUIKA_MULTIPLIER : 1);
}

/**
 * **WHAT PLAYERS CALL THE SUIKADEX** — the profile section listing every species, found or not.
 * `suikadex` stays the code name (routes, components, API); the player reads this, because the
 * story is *The Last Souls* and Soul Dust is what recover each one.
 */
export const SUIKADEX_NAME = 'Recovered Souls';

/**
 * **HOW A NON-STARTER IS OBTAINED, AS A STATUS LABEL** — the one phrase every page that lists a
 * species prints for it (Home, Discover, Collection, the species page). A state, so plain text.
 */
export const FOUND_WITH_SOUL_SHARDS_LABEL = '💠 Found with Soul Dust';

/** Soul Dust needed to find this species. */
export function tracksToFind(species: Pick<SuikamonSpecies, 'rarity'>): number {
  return TRACKS_TO_FIND[species.rarity];
}

/** A species by id, or undefined. */
function speciesById(speciesId: number): SuikamonSpecies | undefined {
  return SUIKAMON_SPECIES.find((species) => species.id === speciesId);
}

function speciesOfRarity(rarity: Rarity): SuikamonSpecies[] {
  return SUIKAMON_SPECIES.filter((species) => species.rarity === rarity);
}

/** The rarities a drop can land in: a positive share AND at least one species. */
function droppableRarities(): Rarity[] {
  return (Object.values(Rarity) as Rarity[]).filter(
    (rarity) => TRACK_DROP_SHARE[rarity] > 0 && speciesOfRarity(rarity).length > 0
  );
}

function droppableShareTotal(): number {
  return droppableRarities().reduce((sum, rarity) => sum + TRACK_DROP_SHARE[rarity], 0);
}

/**
 * **THE SHARE OF ALL DROPS ONE SPECIES GETS**, 0-1, averaged over opponents: its rarity's share
 * split across that rarity's species. (The natural-opponent weight moves drops between species of
 * ONE rarity and never changes the rarity's total, so over many opponents it evens out.)
 */
export function speciesDropShare(species: Pick<SuikamonSpecies, 'rarity'>): number {
  if (!droppableRarities().includes(species.rarity)) return 0;
  return TRACK_DROP_SHARE[species.rarity] / droppableShareTotal() / speciesOfRarity(species.rarity).length;
}

/**
 * **HOW FAST A SPECIES IS FOUND, PER SOUL SHARD DROPPED**: its share of drops ÷ the shards it
 * needs. `1 / findRatePerShard(s)` is how many shards the game must drop in total for one egg of s.
 */
export function findRatePerShard(species: Pick<SuikamonSpecies, 'rarity'>): number {
  return speciesDropShare(species) / TRACKS_TO_FIND[species.rarity];
}

/**
 * **WHICH SPECIES A DROP'S SOUL SHARDS BELONG TO.** Rarity first, by {@link TRACK_DROP_SHARE};
 * then a species of that rarity, the beaten opponent weighted {@link TRACK_NATURAL_WEIGHT}×.
 * Owned or not — whether it becomes Suika is {@link resolveTrackDrop}'s call.
 */
export function pickTrackSpecies(naturalSpeciesId: number, random: () => number): number {
  const rarities = droppableRarities();
  let roll = random() * droppableShareTotal();
  let rarity = rarities[rarities.length - 1];
  for (const candidate of rarities) {
    roll -= TRACK_DROP_SHARE[candidate];
    if (roll < 0) {
      rarity = candidate;
      break;
    }
  }

  const pool = speciesOfRarity(rarity);
  const weightOf = (species: SuikamonSpecies) =>
    species.id === naturalSpeciesId ? TRACK_NATURAL_WEIGHT : 1;
  let pick = random() * pool.reduce((sum, species) => sum + weightOf(species), 0);
  for (const species of pool) {
    pick -= weightOf(species);
    if (pick < 0) return species.id;
  }
  return pool[pool.length - 1].id;
}

/** What one drop of Soul Dust pays: dust toward an egg, or Suika for a duplicate. */
export type TrackDrop =
  | { kind: 'tracks'; speciesId: number; amount: number }
  | { kind: 'suika'; speciesId: number; amount: number; suika: number };

/**
 * **A DUPLICATE BECOMES SUIKA.** Shards of a species the player already owns pay
 * `amount × trackSuikaValue(rarity, source)` Suika instead — a gift's at the gift rate
 * ({@link GIFT_SUIKA_MULTIPLIER}) — and anything else is shards toward its egg.
 */
export function resolveTrackDrop(
  speciesId: number,
  amount: number,
  found: ReadonlySet<number>,
  source: TrackSource = 'drop'
): TrackDrop {
  const species = speciesById(speciesId);
  if (species && found.has(speciesId)) {
    return { kind: 'suika', speciesId, amount, suika: amount * trackSuikaValue(species.rarity, source) };
  }
  return { kind: 'tracks', speciesId, amount };
}

/**
 * **ONE SOUL SHARD PAYOUT, AS THE CLIENT RECEIVES IT** — a Warden's gift on the campaign result.
 * `balance` when the shards were kept toward the egg; `suika` when the player already owns the
 * species and they were paid as Suika instead.
 */
export interface SoulShardGift {
  speciesId: number;
  amount: number;
  balance?: number;
  suika?: number;
}

/** One species' place in the Suikadex. */
export interface SuikadexEntry {
  species: SuikamonSpecies;
  found: boolean;
  tracks: number;
  goal: number;
  /** Shards reached the goal and the species is not found yet — the tile's action is Hatch. */
  ready: boolean;
}

/** Every species in Suikadex order (rarity, then name — the Collection page's order). */
export function buildSuikadex(
  found: ReadonlySet<number>,
  tracks: Readonly<Record<number, number>>
): SuikadexEntry[] {
  const rarityOrder = Object.values(Rarity);
  return [...SUIKAMON_SPECIES]
    .sort(
      (a, b) =>
        rarityOrder.indexOf(a.rarity) - rarityOrder.indexOf(b.rarity) || a.name.localeCompare(b.name)
    )
    .map((species) => {
      const isFound = found.has(species.id);
      const balance = tracks[species.id] ?? 0;
      const goal = tracksToFind(species);
      return { species, found: isFound, tracks: balance, goal, ready: !isFound && balance >= goal };
    });
}
